import { add, getUnixTime, isValid } from 'date-fns';
import { TaskIdentifiers } from '../constants/stock';
import { AppointmentInterface } from '../types/appointment';
import { StockItemInterface } from '../types/stock';
import { TaskType } from '../types/task';

const checkWithinTwoMonths = (date: string) => {
    const checkDate = new Date(date);
    const targetDate = add(new Date(), { months: 2 });

    return (
        isValid(checkDate) && getUnixTime(checkDate) <= getUnixTime(targetDate)
    );
};

const getExaminationDate = (stockFromApi: any, identifier: string): string => {
    return stockFromApi.latestExpirationDates[identifier] || '';
};

export const convertStockItemFromApi = (
    stockFromApi: any,
    appointments: AppointmentInterface[]
): StockItemInterface => {
    const apkDate = getExaminationDate(stockFromApi, TaskIdentifiers.APK);
    const examinationDate = getExaminationDate(
        stockFromApi,
        TaskIdentifiers.EXAMINATION
    );
    const tachographDate = getExaminationDate(
        stockFromApi,
        TaskIdentifiers.TACHOGRAPH
    );
    const tailgateDate = getExaminationDate(
        stockFromApi,
        TaskIdentifiers.TAILGATE
    );
    const cfkExaminationDate = getExaminationDate(
        stockFromApi,
        TaskIdentifiers.CFK_EXAMINATION
    );

    const getAppointmentState = (stockNumber: string) => {
        if (!appointments) {
            return null;
        }

        const appointment = findAppointment(appointments, stockNumber);

        if (!appointment) {
            return null;
        }

        return appointment.state;
    };

    return {
        stockNumber: stockFromApi.stockNumber,
        hasPermanentContract: stockFromApi.hasPermanentContract,
        contractType: stockFromApi.hasPermanentContract ? 'Vast' : 'Tijdelijk',
        chassisNumber: stockFromApi.chassisNumber,
        licensePlate: stockFromApi.vehicle
            ? stockFromApi.vehicle.licensePlate
            : '',
        isSuspended: stockFromApi.isSuspended,
        apkDate: apkDate,
        examinationDate: examinationDate,
        tachographDate: tachographDate,
        tailgateDate: tailgateDate,
        cfkExaminationDate: cfkExaminationDate,
        appointmentState: getAppointmentState(stockFromApi.stockNumber),
        apkDateIsWithinTwoMonths: checkWithinTwoMonths(apkDate),
        tachographDateIsWithinTwoMonths: checkWithinTwoMonths(tachographDate),
        examinationDateIsWithinTwoMonths: checkWithinTwoMonths(examinationDate),
        tailgateDateIsWithinTwoMonths: checkWithinTwoMonths(tailgateDate),
        cfkExaminationDateDateIsWithinTwoMonths:
            checkWithinTwoMonths(cfkExaminationDate),
        imageUrl: stockFromApi.imageUrl,
        customerNote: stockFromApi.customerNote,
        type: stockFromApi.type
            ? stockFromApi.type
            : {
                  id: null,
                  title: '',
                  tasks: [],
              },
        stockTypeTitle: stockFromApi.type ? stockFromApi.type.title : null,
        downloadableCollection: [
            ...stockFromApi.images.map((image: any) => {
                return {
                    ...image,
                    type: 'image',
                };
            }),
            ...stockFromApi.documents.map((document: any) => {
                return {
                    ...document,
                    type: 'document',
                };
            }),
        ],
    };
};

export const getTask = (tasks: TaskType[], identifier: string) => {
    return tasks.find((task) => {
        return task.identifier === identifier;
    });
};

export const sortOnProperty = <T>(
    property: keyof T,
    desc = false,
    isDate = false
) => {
    return (a: T, b: T) => {
        let propA = a[property];
        let propB = b[property];

        // If the property is not available, sort it to the end
        if (!propA) return 1;
        if (!propB) return -1;

        // If it's a date, convert to timestamp for comparison
        if (isDate) {
            // @ts-ignore
            propA = new Date(propA as unknown as string).getTime();
            // @ts-ignore
            propB = new Date(propB as unknown as string).getTime();
        }

        if (propA < propB) {
            return desc ? 1 : -1;
        }
        if (propA > propB) {
            return desc ? -1 : 1;
        }
        return 0;
    };
};

export const filterOnProperty = <T>(
    properties: (keyof T)[],
    value: string,
    data: T[]
) => {
    return data.filter((item) => {
        return properties.some((property) =>
            item[property]
                ?.toString()
                .toLowerCase()
                .includes(value.toLowerCase())
        );
    });
};

export const findAppointment = (
    appointments: AppointmentInterface[],
    stockNumber: string
) => {
    return appointments?.find((appointment) => {
        return appointment.stockNumber === stockNumber;
    });
};

export const findStock = (stock: StockItemInterface[], stockNumber: string) => {
    return (
        stock.find((stockItem) => stockItem.stockNumber === stockNumber) || null
    );
};

export const hasTask = (tasks: TaskType[], identifier: string[]) => {
    return (
        identifier.filter((identifier: string) => {
            return getTask(tasks, identifier);
        }).length > 0
    );
};

export const canUpdateAppointment = (
    appointments: AppointmentInterface[],
    stockNumber: string
) => {
    const appointment = findAppointment(appointments, stockNumber);

    if (!appointment) {
        return false;
    }

    return appointment.rawState === 'open';
};

export const canCreateAppointment = (
    appointments: AppointmentInterface[],
    stockNumber: string
) => {
    const appointment = findAppointment(appointments, stockNumber);

    if (!appointment) {
        return true;
    }

    return !['in_progress', 'open'].includes(appointment.rawState);
};
